"use client";

import React from "react";
import Image from "next/image";
import { Shield, Sparkles, Check, ArrowRight, Zap, Globe } from "lucide-react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { Logo } from "@/components/ui";

function cn(...classes: Array<string | false | undefined | null>) {
  return classes.filter(Boolean).join(" ");
}

function Chip({
  icon,
  text,
}: {
  icon: React.ReactNode;
  text: string;
}) {
  return (
    <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.06] px-3 py-1 text-xs text-white/70">
      <span className="text-white/45">{icon}</span>
      <span>{text}</span>
    </div>
  );
}

function GhostButton({
  children,
  href,
}: {
  children: React.ReactNode;
  href: string;
}) {
  return (
    <Link
      href={href}
      className={cn(
        "h-9 rounded-xl px-3",
        "border border-white/10 bg-white/[0.03]",
        "text-sm font-medium text-white/75",
        "hover:bg-white/[0.06] hover:text-white",
        "transition flex items-center justify-center"
      )}
    >
      {children}
    </Link>
  );
}

interface AuthShellProps {
  children: React.ReactNode;
}

export function AuthShell({ children }: AuthShellProps) {
  const pathname = usePathname();
  const isSignup = pathname === "/auth/signup";
  const isLogin = pathname === "/auth/login";

  return (
    <div className="min-h-screen w-full bg-slate-950 text-white">
      {/* background accents */}
      <div className="pointer-events-none fixed inset-0">
        <div className="absolute -left-40 -top-40 h-[520px] w-[520px] rounded-full bg-emerald-500/10 blur-3xl" />
        <div className="absolute -right-40 top-20 h-[520px] w-[520px] rounded-full bg-cyan-500/10 blur-3xl" />
        <div className="absolute bottom-0 left-1/2 h-[520px] w-[820px] -translate-x-1/2 rounded-full bg-violet-500/10 blur-3xl" />
        <div
          className="absolute inset-0 opacity-[0.06]"
          style={{
            backgroundImage:
              "radial-gradient(circle at 1px 1px, rgba(255,255,255,0.55) 1px, transparent 0)",
            backgroundSize: "28px 28px",
          }}
        />
      </div>

      <div className="relative mx-auto flex min-h-screen max-w-6xl flex-col px-4 py-10 md:px-10">
        {/* top bar */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Logo
              variant="full"
              size="md"
              className="h-8"
            />
          </div>

          <div className="flex items-center gap-2">
            {isLogin && (
              <div className="hidden md:flex items-center gap-2">
                <Chip icon={<Check className="h-3.5 w-3.5" />} text="Verified Domain" />
                <Chip icon={<Globe className="h-3.5 w-3.5" />} text="AU / Sydney" />
              </div>
            )}
            {!isLogin && (
              <div className="hidden md:flex items-center gap-2">
                <Chip icon={<Sparkles className="h-3.5 w-3.5" />} text="Next Level UI" />
                <Chip icon={<Check className="h-3.5 w-3.5" />} text="Secure sign-in" />
              </div>
            )}
            <GhostButton href={isSignup ? "/auth/login" : "/auth/signup"}>
              {isSignup ? "Sign in" : "Create account"}
            </GhostButton>
          </div>
        </div>

        {/* main */}
        <div className="flex flex-1 items-center justify-center py-12">
          <div className="grid w-full items-center gap-10 md:grid-cols-2">
            {/* left marketing */}
            <div className="hidden md:block">
              <div className="max-w-md">
                {isLogin ? (
                  <>
                    {/* Login Marketing Content */}
                    <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] px-3 py-1 text-xs text-white/70">
                      <Sparkles className="h-3.5 w-3.5 text-emerald-300" />
                      Next-level scheduling, compliance, and ops control
                    </div>
                    <h1 className="mt-4 text-4xl font-semibold tracking-tight">
                      Sign in to run your workforce like a command centre.
                    </h1>
                    <p className="mt-4 text-white/60">
                      One login for rostering, time & attendance, incidents, mobile patrol, and reporting. Built for speed, clarity, and audit-ready operations.
                    </p>

                    {/* Feature Chips - Horizontal */}
                    <div className="mt-6 flex flex-wrap gap-2">
                      <div className="inline-flex items-center gap-2 rounded-xl border border-white/10 bg-white/[0.04] px-4 py-2.5">
                        <ArrowRight className="h-4 w-4 text-emerald-300" />
                        <div>
                          <div className="text-xs font-medium">Fast</div>
                          <div className="text-xs text-white/50">Minimal clicks, maximum flow</div>
                        </div>
                      </div>
                      <div className="inline-flex items-center gap-2 rounded-xl border border-white/10 bg-white/[0.04] px-4 py-2.5">
                        <Shield className="h-4 w-4 text-emerald-300" />
                        <div>
                          <div className="text-xs font-medium">Secure</div>
                          <div className="text-xs text-white/50">Role-based access control</div>
                        </div>
                      </div>
                      <div className="inline-flex items-center gap-2 rounded-xl border border-white/10 bg-white/[0.04] px-4 py-2.5">
                        <Zap className="h-4 w-4 text-emerald-300" />
                        <div>
                          <div className="text-xs font-medium">Reliable</div>
                          <div className="text-xs text-white/50">Designed for 24/7 ops</div>
                        </div>
                      </div>
                    </div>

                    {/* Tip for teams box */}
                    <div className="mt-6 rounded-xl border border-white/10 bg-white/[0.04] p-4">
                      <div className="text-xs font-medium text-white/70">Tip for teams</div>
                      <div className="mt-1 text-xs text-white/50">
                        Use SSO for admins and Magic Link for contractors to reduce password resets.
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    {/* Signup Marketing Content */}
                    <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] px-3 py-1 text-xs text-white/70">
                      <span className="h-2 w-2 rounded-full bg-emerald-400" />
                      Tenant-first onboarding
                    </div>
                    <h1 className="mt-4 text-4xl font-semibold tracking-tight">
                      Run your operations
                      <span className="text-emerald-300"> at scale</span>.
                    </h1>
                    <p className="mt-4 text-white/60">
                      Create your organisation, enable modules, and onboard staff — all
                      in one system.
                    </p>

                    <div className="mt-8 grid gap-3">
                      {["Secure access controls", "Module-based pricing", "Fast onboarding flow"].map(
                        (t) => (
                          <div
                            key={t}
                            className="flex items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.04] p-4"
                          >
                            <div className="grid h-10 w-10 place-items-center rounded-xl bg-emerald-500/10 ring-1 ring-emerald-400/15">
                              <Check className="h-5 w-5 text-emerald-300" />
                            </div>
                            <div>
                              <div className="text-sm font-medium">{t}</div>
                              <div className="text-xs text-white/50">
                                Built for multi-tenant workforce operations.
                              </div>
                            </div>
                          </div>
                        )
                      )}
                    </div>

                    {/* Tip for teams box */}
                    <div className="mt-6 rounded-xl border border-white/10 bg-white/[0.04] p-4">
                      <div className="text-xs font-medium text-white/70">Tip for teams</div>
                      <div className="mt-1 text-xs text-white/50">
                        Use your company&apos;s tenant slug to access your organization&apos;s workspace.
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* right auth panel */}
            <div className="mx-auto w-full max-w-md">
              {children}
              {isLogin && (
                <div className="mt-4 text-center text-xs text-white/45">
                  By continuing, you agree to your organization&apos;s acceptable use policy and acknowledge audit logging.
                </div>
              )}
              {!isLogin && (
                <div className="mt-4 text-center text-xs text-white/45">
                  By continuing, you agree to your organisation&apos;s security policy and acknowledge that all actions are logged for audit purposes.
                </div>
              )}
            </div>
          </div>
        </div>

        {/* footer */}
        <div className="flex items-center justify-center text-xs text-white/40">
          <div>© {new Date().getFullYear()} Ops Next Level • Built for 24/7 operations</div>
        </div>
      </div>
    </div>
  );
}

